import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/layout/Navbar';
import Header from '@/components/layout/Header';
import CourseCard, { CourseData } from '@/components/ui/course-card/CourseCard';
import { Filter, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fetchCourses, fetchPublicCourses, validateCourseAccess } from '@/services/courses';
import { getProgressOverview } from '@/services/progress';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const Courses: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [showDrafts, setShowDrafts] = useState(false);
  const [courses, setCourses] = useState<CourseData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [hasAccess, setHasAccess] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();
  
  useEffect(() => {
    async function loadCourses() {
      setIsLoading(true);
      
      try {
        if (isAuthenticated) {
          // Authenticated user: validate access and fetch full course details
          const canAccess = await validateCourseAccess('User');
          
          if (!canAccess) {
            setHasAccess(false);
            setIsLoading(false);
            
            toast({
              title: "Access denied",
              description: "You don't have permission to view courses",
              variant: "destructive"
            });
            
            return;
          }
          
          // Get all courses for authenticated user
          const courseResponse = await fetchCourses();
          
          if (!courseResponse || !courseResponse.courses || courseResponse.courses.length === 0) {
            setCourses([]);
            setTotalCount(0);
            setIsLoading(false);
            return;
          }
          
          setTotalCount(courseResponse.totalCount);
          
          // Get progress information
          let progressData: Record<string, { progress: number, completedLessons: number }> = {};
          try {
            const progressOverview = await getProgressOverview();
            if (progressOverview && progressOverview.courseProgress) {
              // Create a map of course ID to progress data
              progressOverview.courseProgress.forEach(item => {
                progressData[item.courseId] = {
                  progress: item.progress,
                  completedLessons: item.completedLessons
                };
              });
            }
          } catch (error) {
            console.error('Error fetching progress:', error);
            // Continue without progress data
          }
          
          // Transform the data to match CourseData structure
          const formattedCourses = courseResponse.courses.map(course => {
            // Get progress for this course
            const courseProgress = progressData[course.id] || { progress: 0, completedLessons: 0 };
            
            // Calculate if the course is completed
            const totalLessons = course.lessonCount || (course.lessons?.length || 0);
            const isCompleted = totalLessons > 0 && courseProgress.completedLessons === totalLessons;
            
            return {
              id: course.id,
              title: course.title,
              description: course.description || '',
              coverImage: course.imageUrl || course.coverImage || "/thumbnail.webp",
              totalLessons: totalLessons,
              estimatedTime: course.estimatedTime || calculateEstimatedTime(course.estimatedDuration || 0),
              progress: courseProgress.progress || 0,
              completed: isCompleted,
              status: course.status === 'published' ? 'published' as const : (course.isPublished ? 'published' as const : 'draft' as const)
            };
          });
          
          setCourses(formattedCourses);
        } else {
          // Not authenticated: fetch public courses with limited info
          const publicCourseResponse = await fetchPublicCourses();
          
          if (!publicCourseResponse || !publicCourseResponse.courses || publicCourseResponse.courses.length === 0) {
            setCourses([]);
            setTotalCount(0);
            setIsLoading(false);
            return;
          }
          
          setTotalCount(publicCourseResponse.totalCount);
          
          // Transform the data to match CourseData structure
          const formattedCourses = publicCourseResponse.courses.map(course => {
            // Access imageUrl directly - it's coming correctly from the API
            const courseImageUrl = course.imageUrl || course.coverImage;
            
            return {
              id: course.id,
              title: course.title,
              description: course.description || '',  // Use the description from the public endpoint
              coverImage: courseImageUrl || "/thumbnail.webp", // Use the image URL from the API
              totalLessons: course.lessonCount || 0,
              estimatedTime: course.estimatedTime || 'No duration set',
              progress: 0,
              completed: false,
              status: 'published' as const
            };
          });
          
          setCourses(formattedCourses);
        }
      } catch (error) {
        console.error('Error loading courses:', error);
        toast({
          title: "Error loading courses",
          description: "Failed to load courses. Please try again later.",
          variant: "destructive"
        });
        setCourses([]);
      } finally {
        setIsLoading(false);
      }
    }
    
    loadCourses();
  }, [toast, navigate, isAuthenticated]);
  
  // Helper function to format estimated duration from minutes to readable string
  const calculateEstimatedTime = (minutes: number): string => {
    if (minutes === 0) {
      return 'No duration set';
    } else if (minutes < 60) {
      return `${minutes} min`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      
      if (remainingMinutes === 0) {
        return `${hours} hour${hours > 1 ? 's' : ''}`;
      } else {
        return `${hours} hour${hours > 1 ? 's' : ''} ${remainingMinutes} min`;
      }
    }
  };
  
  // Filter courses - Only include published courses by default
  // Only include drafts if user is admin AND showDrafts toggle is enabled
  const availableCourses = courses.filter(course => 
    course.status === 'published' || (user?.role === 'admin' && showDrafts && course.status === 'draft')
  );

  // Filter courses based on search term
  const filteredCourses = availableCourses.filter(course => 
    course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!hasAccess) {
    return (
      <div className="flex min-h-screen flex-col">
        <Navbar />
        <main className="flex-1 container py-6">
          <Alert variant="destructive">
            <AlertTitle>Access Denied</AlertTitle>
            <AlertDescription>
              You don't have permission to view this page. Please log in with appropriate credentials.
            </AlertDescription>
          </Alert>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <Navbar />
      
      <main className="flex-1 container py-6">
        <Header 
          title="All Courses" 
          description="Explore our full catalog of courses" 
        />
        
        {/* Search and filters */}
        <div className="mb-8 flex flex-col sm:flex-row gap-4 justify-between items-start">
          <div className="relative max-w-lg w-full">
            <input
              type="text"
              placeholder="Search courses..."
              className="w-full rounded-md border border-input bg-background px-4 py-2 pr-10 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <svg
              className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.3-4.3"></path>
            </svg>
          </div>
          
          {/* Only show draft toggle for admins */}
          {user?.role === 'admin' && (
            <Button 
              variant={showDrafts ? "default" : "outline"} 
              size="sm"
              onClick={() => setShowDrafts(!showDrafts)}
              className="flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              {showDrafts ? "Showing drafts" : "Show draft courses"}
            </Button>
          )}
        </div>
        
        {/* Loading state */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2">Loading courses...</span>
          </div>
        )}
        
        {/* Courses Grid */}
        {!isLoading && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredCourses.map(course => (
              <CourseCard 
                key={course.id} 
                course={course} 
                isClickable={isAuthenticated}
              />
            ))}
          </div>
        )}
        
        {/* No Results */}
        {!isLoading && filteredCourses.length === 0 && (
          <div className="mt-8 text-center">
            <p className="text-lg font-medium">No courses found</p>
            <p className="text-muted-foreground">
              {searchTerm ? 'Try adjusting your search terms' : 'No courses are available at this time'}
            </p>
          </div>
        )}
        
        {/* Pagination info - if needed in the future */}
        {totalCount > 0 && (
          <div className="mt-6 text-sm text-muted-foreground text-right">
            Showing {filteredCourses.length} of {totalCount} courses
          </div>
        )}
      </main>
    </div>
  );
};

export default Courses;
